import type { ComponentType, SVGProps } from 'react';
import { MapPin, Phone, CheckSquare, AlertTriangle } from 'lucide-react';
import type { TodayGlance } from '../../types';

type IconC = ComponentType<SVGProps<SVGSVGElement>>;

function timeOfDayGreeting(d: Date): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function GlanceStat({ Icon, n, label }: { Icon: IconC; n: number; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-200">
      <Icon className="h-4 w-4 text-blast-600 dark:text-blast-400" />
      <span className="font-semibold tabular-nums">{n}</span>
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
    </span>
  );
}

/**
 * The morning hero — greeting + a compact one-line "today at a glance" summary
 * with overdue emphasized. Mobile-first: a single tinted card, not a tall stat
 * strip. Empty days get a directive line instead of a wall of zeros.
 */
export function TodayHero({
  glance,
  userName,
  followUpDue,
}: {
  glance: TodayGlance;
  userName: string;
  followUpDue: number;
}) {
  const now = new Date();
  const firstName = (userName || '').trim().split(/\s+/)[0] || 'there';
  const dateLine = now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const hasToday = glance.visitsToday + glance.callsToday + glance.tasksDueToday > 0;

  return (
    <div className="card p-5 sm:p-6 border-l-4 border-l-blast-500 dark:border-l-blast-400">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">
        {dateLine}
      </p>
      <h1 className="mt-0.5 text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
        {timeOfDayGreeting(now)}, {firstName}
      </h1>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        {hasToday ? (
          <>
            <GlanceStat Icon={MapPin} n={glance.visitsToday} label={glance.visitsToday === 1 ? 'visit' : 'visits'} />
            <GlanceStat Icon={Phone} n={glance.callsToday} label={glance.callsToday === 1 ? 'call' : 'calls'} />
            <GlanceStat Icon={CheckSquare} n={glance.tasksDueToday} label={glance.tasksDueToday === 1 ? 'task' : 'tasks'} />
          </>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">Nothing scheduled today.</span>
        )}
        {glance.overdueCount > 0 && (
          <span className="inline-flex items-center gap-1.5 font-semibold text-rose-600 dark:text-rose-400">
            <AlertTriangle className="h-4 w-4" />
            {glance.overdueCount} overdue
          </span>
        )}
      </div>

      {glance.loggedToday > 0 && (
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          You've logged {glance.loggedToday} {glance.loggedToday === 1 ? 'activity' : 'activities'} today — nice.
        </p>
      )}
      {!hasToday && glance.overdueCount === 0 && followUpDue > 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {followUpDue} follow-up{followUpDue === 1 ? '' : 's'} waiting when you're ready.
        </p>
      )}
    </div>
  );
}
